/**
 * TodayBirthday (Dashboard widget)
 *
 * Calls GET /v1/organization/workers/birthdays?date=today (Wave 7.2
 * backend endpoint) and renders a list of workers whose DOB matches
 * today's month+day.
 *
 * Response shape: BirthdaysResponse = { date, workers: BirthdayWorker[] }.
 * BirthdayWorker = { id, first_name, last_name, avatar_url, date_of_birth }.
 *
 * Age is computed client-side from date_of_birth. Department / position
 * are intentionally omitted — BirthdayWorker is a lightweight shape;
 * wiring richer joins on this widget isn't worth it.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Chip,
  CircularProgress,
  useTheme,
} from '@mui/material';
import { CakeOutlined, PersonOutlined } from '@mui/icons-material';

import { ApiError, organizationApi, type Schema } from '../../../../api';
import { WidgetErrorState } from '../../../../components/primitives';
import { useTranslation } from 'react-i18next';
import { formatDashboardDate } from './widgetDisplay';
import { translateApiError } from '../../../../i18n/translateApiError';
import WidgetEmptyState from '../../../../components/primitives/WidgetEmptyState';

type BirthdaysResponse = Schema<'BirthdaysResponse'>;
type BirthdayWorker = Schema<'BirthdayWorker'>;

/** Compute age on the birthday date from DOB (both ISO YYYY-MM-DD). */
function ageOnDate(dob: string, onDate: string): number | null {
  try {
    const [dY, dM, dD] = dob.split('-').map(Number);
    const [oY, oM, oD] = onDate.split('-').map(Number);
    if (!dY || !dM || !dD || !oY || !oM || !oD) return null;
    let age = oY - dY;
    if (oM < dM || (oM === dM && oD < dD)) age -= 1;
    return age;
  } catch {
    return null;
  }
}

const TodayBirthday: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const [payload, setPayload] = useState<BirthdaysResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchBirthdays = useCallback(async () => {
    setLoading(true);
    try {
      // No argument → backend resolves to server today (ISO).
      const data = await organizationApi.workersApi.birthdays();
      setPayload(data);
      setError(null);
    } catch (e) {
      setError(
        e instanceof ApiError ? translateApiError(e, t) : t('dashboard:widgets.todayBirthday.loadError'),
      );
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchBirthdays();
  }, [fetchBirthdays, t]);

  const workers = useMemo<BirthdayWorker[]>(
    () => payload?.workers ?? [],
    [payload],
  );

  const headerDate = useMemo(() => {
    const iso = payload?.date ?? new Date().toISOString().slice(0, 10);
    return formatDashboardDate(new Date(iso + 'T00:00:00'), i18n.language, {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
    });
  }, [i18n.language, payload]);

  // Render -----------------------------------------------------------

  if (loading && !payload) {
    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (error && !payload) {
    return <WidgetErrorState error={error} onRetry={fetchBirthdays} />;
  }

  if (workers.length === 0) {
    return (
      <WidgetEmptyState
        icon={CakeOutlined}
        title={t('dashboard:widgets.todayBirthday.emptyTitle')}
        hint={t('dashboard:widgets.todayBirthday.emptyHint')}
      />
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header — no px of its own. The widget body container in
          Dashboard.tsx already applies p: 2, matching the shared title bar's
          px: 2, so an extra px: 1.5 here indented this card's summary line
          12px further than its own title and than every other widget's
          content. py stays: the vertical gap is this row's business. */}
      <Box sx={{ pt: 0, pb: 1 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              size="small"
              label={t('dashboard:widgets.todayBirthday.birthdayCount', { count: workers.length })}
              color="primary"
              variant="outlined"
              sx={{
                height: 24,
                '& .MuiChip-label': { px: 1, fontSize: '0.75rem' },
              }}
            />
            <Typography variant="caption" color="text.secondary">
              {headerDate}
            </Typography>
          </Box>
          <CakeOutlined
            sx={{ color: theme.palette.primary.main, fontSize: 18 }}
          />
        </Box>
      </Box>

      {/* List */}
      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        <List sx={{ py: 0, mt: -2 }}>
          {workers.map((worker, index) => {
            const fullName =
              `${worker.first_name} ${worker.last_name}`.trim() || t('dashboard:widgets.todayBirthday.unnamed');
            const age = ageOnDate(
              worker.date_of_birth,
              payload?.date ?? new Date().toISOString().slice(0, 10),
            );
            return (
              <ListItem
                key={worker.id}
                component={Paper}
                elevation={0}
                onClick={() => navigate(
              // `worker=<name>`, not `worker_id`: this page reads `worker` and its filter is
              // a text search, so an id matched nothing and the click landed on the full
              // list. Same parameter the two other drill-throughs into worker lists use.
              `/organization/workers/active?worker=${encodeURIComponent(fullName)}`,
            )}
                sx={{
                  py: 1,
                  mb: index === workers.length - 1 ? 0 : 0.5,
                  mt: index === 0 ? 3 : 0,
                  borderRadius: 1,
                  bgcolor: 'background.paper',
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  border: '1px solid transparent',
                  transition: 'border-color 120ms',
                  '&:hover': { borderColor: 'divider' },
                }}
              >
                <ListItemAvatar>
                  <Avatar
                    src={worker.avatar_url || undefined}
                    sx={{
                      width: 40,
                      height: 40,
                      border: `2px solid ${theme.palette.primary.main}`,
                    }}
                  >
                    <PersonOutlined />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 600,
                          color: 'primary.main',
                          '&:hover': { textDecoration: 'underline' },
                        }}
                      >
                        {fullName}
                      </Typography>
                      {age !== null && (
                        <Chip
                          size="small"
                          label={t('dashboard:widgets.todayBirthday.ageYears', { count: age })}
                          color="secondary"
                          variant="filled"
                          sx={{
                            height: 20,
                            '& .MuiChip-label': {
                              px: 1,
                              fontSize: '0.70rem',
                              fontWeight: 500,
                            },
                            backgroundColor: theme.palette.warning.light,
                            color: theme.palette.warning.contrastText,
                          }}
                        />
                      )}
                    </Box>
                  }
                  secondary={
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: '0.75rem' }}
                    >
                      {formatDashboardDate(
                        new Date(worker.date_of_birth + 'T00:00:00'),
                        i18n.language,
                        { year: 'numeric', month: 'long', day: 'numeric' },
                      )}
                    </Typography>
                  }
                />
              </ListItem>
            );
          })}
        </List>
      </Box>
    </Box>
  );
};

export default TodayBirthday;
